import type { Module, ModuleLoader } from './module-types';
import type { MenuItem, AppConfig } from '../config/app-configs';

class ModuleRegistry {
  private modules: Map<string, Module> = new Map();
  private loaders: Map<string, ModuleLoader> = new Map();
  private loadedModules: Set<string> = new Set();

  /**
   * Modül loader'ını kaydet
   */
  registerLoader(moduleId: string, loader: ModuleLoader): void {
    this.loaders.set(moduleId, loader);
  }

  /** `registerLoader` ile kayıtlı modül kimlikleri (async yüklemeden önce route eşlemesi için). */
  getRegisteredModuleIds(): string[] {
    return Array.from(this.loaders.keys());
  }

  /** Router-relative modül kökü; mevcut index route'u modülün ana sayfasını seçer. */
  getModuleRootPath(pathname: string): string {
    const segments = pathname.split('/').filter(Boolean);
    // /erp/:module eski route alias'ıdır; dağıtım basename'ini router yönetir.
    const moduleId = segments[0] === 'erp' ? segments[1] : segments[0];
    return moduleId && this.loaders.has(moduleId) ? `/${moduleId}` : '/';
  }

  /**
   * Modülü yükle ve kaydet
   */
  async loadModule(moduleId: string): Promise<Module | null> {
    // Zaten yüklenmişse cache'den dön
    if (this.loadedModules.has(moduleId)) {
      return this.modules.get(moduleId) || null;
    }

    const loader = this.loaders.get(moduleId);
    if (!loader) {
      console.warn(`Module loader not found for: ${moduleId}`);
      return null;
    }

    try {
      const moduleData = await loader();
      const module = moduleData.default;
      
      // Modülü kaydet
      this.modules.set(moduleId, module);
      this.loadedModules.add(moduleId);

      // CSS'leri yükle
      if (module.loadStyles) {
        await module.loadStyles();
      }

      return module;
    } catch (error) {
      console.error(`Failed to load module ${moduleId}:`, error);
      return null;
    }
  }

  /**
   * Tüm modülleri yükle
   */
  async loadAllModules(): Promise<Module[]> {
    const modules: Module[] = [];
    
    for (const [moduleId] of this.loaders) {
      const module = await this.loadModule(moduleId);
      if (module) {
        modules.push(module);
      }
    }

    return modules;
  }

  /**
   * Modülü al (yüklenmişse)
   */
  getModule(moduleId: string): Module | undefined {
    return this.modules.get(moduleId);
  }

  /**
   * Tüm yüklenmiş modülleri al
   */
  getAllModules(): Module[] {
    return Array.from(this.modules.values());
  }

  /**
   * Modül yüklü mü kontrol et
   */
  isModuleLoaded(moduleId: string): boolean {
    return this.loadedModules.has(moduleId);
  }

  /**
   * Tüm modül route'larını al
   */
  getAllRoutes(): Array<{ moduleId: string; route: { path: string; component: any; exact?: boolean } }> {
    const routes: Array<{ moduleId: string; route: { path: string; component: any; exact?: boolean } }> = [];
    
    for (const [moduleId, module] of this.modules) {
      for (const route of module.routes) {
        routes.push({ moduleId, route });
      }
    }

    return routes;
  }

  /**
   * Tüm modül menü öğelerini al
   */
  getAllMenuItems(): MenuItem[] {
    const menuItems: MenuItem[] = [];
    
    for (const module of this.modules.values()) {
      menuItems.push(...module.menuItems);
    }

    return menuItems;
  }

  /**
   * Belirli bir modülün menü öğelerini al
   */
  getModuleMenuItems(moduleId: string): MenuItem[] {
    const module = this.modules.get(moduleId);
    return module?.menuItems || [];
  }

  /**
   * Modül config'ini al
   */
  getModuleConfig(moduleId: string): AppConfig | undefined {
    return this.modules.get(moduleId)?.config;
  }
}

// Singleton instance
export const moduleRegistry = new ModuleRegistry();
